/**
 * BoundingBoxOverlay
 *
 * SVG overlay component that renders bounding box highlights over a document.
 * Supports hover and locked states with bi-directional interaction.
 */

import { useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import type {
  Citation,
  HighlightState,
  PageDimensions,
  PolygonPoint,
} from '../types';
import { polygonToPercentages } from '../hooks/useCoordinateConversion';

interface BoundingBoxOverlayProps {
  /** Citations to render on the current page */
  citations: Citation[];
  /** Dimensions of the current page (for coordinate conversion) */
  pageDimensions: PageDimensions;
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Currently hovered field path */
  hoveredFieldPath: string | null;
  /** Currently locked field path */
  lockedFieldPath: string | null;
  /** Map from citation to field path */
  citationFieldMap: Map<Citation, string>;
  /** Callback when a citation is hovered */
  onCitationHover: (
    citation: Citation | null,
    fieldPath: string | null,
  ) => void;
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation, fieldPath: string) => void;
  /** Current scale/zoom level */
  scale?: number;
}

/**
 * Get the highlight state for a citation based on current hover/lock state.
 */
function getCitationHighlightState(
  citation: Citation,
  citationFieldMap: Map<Citation, string>,
  hoveredFieldPath: string | null,
  lockedFieldPath: string | null,
): HighlightState {
  const fieldPath = citationFieldMap.get(citation);

  if (fieldPath === lockedFieldPath) {
    return 'locked';
  }

  if (fieldPath === hoveredFieldPath) {
    return 'hovered';
  }

  return 'default';
}

/**
 * Get SVG polygon fill and stroke styles based on highlight state.
 */
function getPolygonStyles(state: HighlightState): {
  fill: string;
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
  opacity: number;
} {
  switch (state) {
    case 'locked':
      return {
        fill: 'none',
        stroke: '#3b82f6', // blue-500
        strokeWidth: 0.3,
        strokeDasharray: '2 1',
        opacity: 1,
      };
    case 'hovered':
      return {
        fill: 'none',
        stroke: '#3b82f6', // blue-500
        strokeWidth: 0.25,
        opacity: 1,
      };
    default:
      return {
        fill: 'transparent',
        stroke: 'transparent',
        strokeWidth: 0,
        opacity: 0,
      };
  }
}

export function BoundingBoxOverlay({
  citations,
  pageDimensions,
  currentPage,
  hoveredFieldPath,
  lockedFieldPath,
  citationFieldMap,
  onCitationHover,
  onCitationClick,
}: BoundingBoxOverlayProps) {
  // Filter citations for the current page
  const currentPageCitations = useMemo(
    () => citations.filter((c) => c.page === currentPage),
    [citations, currentPage],
  );

  // Convert polygon coordinates to percentages for SVG rendering
  const citationsWithPercentages = useMemo(() => {
    return currentPageCitations.map((citation) => {
      // Use citation's own page dimensions if available, otherwise fall back to passed dimensions
      const dims: PageDimensions =
        citation.pageWidth && citation.pageHeight
          ? { width: citation.pageWidth, height: citation.pageHeight }
          : pageDimensions;

      return {
        citation,
        percentagePolygon: polygonToPercentages(citation.polygon, dims),
        highlightState: getCitationHighlightState(
          citation,
          citationFieldMap,
          hoveredFieldPath,
          lockedFieldPath,
        ),
      };
    });
  }, [
    currentPageCitations,
    pageDimensions,
    citationFieldMap,
    hoveredFieldPath,
    lockedFieldPath,
  ]);

  const handleMouseEnter = useCallback(
    (citation: Citation) => {
      const fieldPath = citationFieldMap.get(citation) ?? null;
      onCitationHover(citation, fieldPath);
    },
    [citationFieldMap, onCitationHover],
  );

  const handleMouseLeave = useCallback(() => {
    onCitationHover(null, null);
  }, [onCitationHover]);

  const handleClick = useCallback(
    (citation: Citation) => {
      const fieldPath = citationFieldMap.get(citation);
      if (fieldPath) {
        onCitationClick(citation, fieldPath);
      }
    },
    [citationFieldMap, onCitationClick],
  );

  if (currentPageCitations.length === 0) {
    return null;
  }

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ width: '100%', height: '100%' }}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {citationsWithPercentages.map(
        ({ citation, percentagePolygon, highlightState }, index) => {
          const styles = getPolygonStyles(highlightState);
          const points = percentagePolygon
            .map((p) => `${p.x},${p.y}`)
            .join(' ');

          return (
            <polygon
              key={`${citation.page}-${index}`}
              points={points}
              fill={styles.fill}
              stroke={styles.stroke}
              strokeWidth={styles.strokeWidth}
              strokeDasharray={styles.strokeDasharray}
              opacity={styles.opacity}
              className={cn(
                'transition-all duration-150',
                // Enable pointer events only when there's something to interact with
                highlightState !== 'default' &&
                  'pointer-events-auto cursor-pointer',
              )}
              onMouseEnter={() => handleMouseEnter(citation)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(citation)}
            />
          );
        },
      )}
    </svg>
  );
}

/**
 * Simplified overlay that shows all bounding boxes with minimal styling.
 * Useful for debugging or showing all extraction locations at once.
 */
export function BoundingBoxDebugOverlay({
  citations,
  pageDimensions,
  currentPage,
}: {
  citations: Citation[];
  pageDimensions: PageDimensions;
  currentPage: number;
}) {
  const currentPageCitations = useMemo(
    () => citations.filter((c) => c.page === currentPage),
    [citations, currentPage],
  );

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ width: '100%', height: '100%' }}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {currentPageCitations.map((citation, index) => {
        // Use citation's own page dimensions if available
        const dims: PageDimensions =
          citation.pageWidth && citation.pageHeight
            ? { width: citation.pageWidth, height: citation.pageHeight }
            : pageDimensions;

        const percentagePolygon = polygonToPercentages(citation.polygon, dims);
        const points = percentagePolygon.map((p) => `${p.x},${p.y}`).join(' ');

        return (
          <polygon
            key={`debug-${citation.page}-${index}`}
            points={points}
            fill="hsl(var(--primary) / 0.1)"
            stroke="hsl(var(--primary) / 0.5)"
            strokeWidth={0.5}
          />
        );
      })}
    </svg>
  );
}
